import React, { useEffect, useRef, useCallback, useState } from 'react';
import { GoogleMap, LoadScriptNext, Marker } from '@react-google-maps/api';
import './ParcelMap.css';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const mapOptions = {
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: true,
  mapTypeControlOptions: {
    mapTypeIds: ['roadmap', 'satellite', 'hybrid'],
    position: window.google?.maps?.ControlPosition?.TOP_RIGHT,
  },
  mapTypeId: 'satellite',
  zoomControl: true,
  tilt: 0,
  rotateControl: false,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
  ],
};

const ParcelMap = React.forwardRef(
  (
    {
      parcelGeometry,
      address,
      coordinates,
      height = '400px',
      onBoundaryUpdate,
    },
    ref
  ) => {
    const mapRef = useRef(null);
    const [offset, setOffset] = useState({ lat: 0, lng: 0 });
    const [map, setMap] = useState(null);
    const [distanceLabels, setDistanceLabels] = useState([]);
    const [measurementMarkers, setMeasurementMarkers] = useState([]);
    const polygonRef = useRef(null);

    // Function to check if three points are nearly collinear
    const arePointsCollinear = (p1, p2, p3, tolerance = 0.000001) => {
      // Calculate the area of the triangle formed by the three points
      // If the area is very small, the points are nearly collinear
      const area = Math.abs(
        (p2[0] - p1[0]) * (p3[1] - p1[1]) - (p3[0] - p1[0]) * (p2[1] - p1[1])
      );

      // Calculate the distance between p1 and p3
      const distance = Math.sqrt(
        Math.pow(p3[0] - p1[0], 2) + Math.pow(p3[1] - p1[1], 2)
      );

      // Normalize the area by the distance to get a measure of deviation
      const deviation = distance > 0 ? area / distance : 0;

      return deviation < tolerance;
    };

    // Function to simplify polygon by removing nearly collinear points
    const simplifyPolygon = (points) => {
      if (!points || points.length <= 3) return points;

      const simplified = [];
      let i = 0;

      // Always keep the first point
      simplified.push(points[0]);

      while (i < points.length - 2) {
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[i + 2];

        if (arePointsCollinear(p1, p2, p3)) {
          // Skip the middle point (p2) as it's collinear
          i += 2;
          // If this is the last segment, add the last point
          if (i === points.length - 1) {
            simplified.push(points[i]);
          }
        } else {
          // Points are not collinear, keep the next point
          simplified.push(points[i + 1]);
          i += 1;
        }
      }

      // Add any remaining points
      if (i === points.length - 2) {
        simplified.push(points[points.length - 2]);
      }

      // Ensure the polygon is closed (last point same as first)
      if (
        points.length > 0 &&
        points[points.length - 1][0] === points[0][0] &&
        points[points.length - 1][1] === points[0][1]
      ) {
        simplified.push(points[0]);
      }

      return simplified;
    };

    // Default center on LA if no coordinates
    const defaultCenter = coordinates
      ? { lat: coordinates.lat, lng: coordinates.lng }
      : { lat: 34.0522, lng: -118.2437 }; // LA downtown

    // Calculate bounds for the parcel
    const getParcelBounds = useCallback(() => {
      if (!parcelGeometry || !parcelGeometry.rings) return null;

      const bounds = new window.google.maps.LatLngBounds();
      parcelGeometry.rings[0].forEach((point) => {
        bounds.extend({
          lat: point[1] + offset.lat,
          lng: point[0] + offset.lng,
        });
      });

      return bounds;
    }, [parcelGeometry, offset]);

    // Calculate distance labels
    useEffect(() => {
      if (!parcelGeometry || !map) return;

      // Clear existing labels, markers, and polygon
      distanceLabels.forEach((label) => label.setMap(null));
      measurementMarkers.forEach((marker) => marker.setMap(null));
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }

      // Draw polygon imperatively (React Polygon component has rendering issues)
      const polygonRing = parcelGeometry.rings[0];
      if (polygonRing && polygonRing.length > 0) {
        const polyPaths = polygonRing.map((point) => ({
          lat: point[1] + offset.lat,
          lng: point[0] + offset.lng,
        }));
        polygonRef.current = new window.google.maps.Polygon({
          paths: polyPaths,
          strokeColor: '#FF0000',
          strokeOpacity: 0.8,
          strokeWeight: 3,
          fillColor: '#FF0000',
          fillOpacity: 0.1,
          map: map,
        });
      }

      const newLabels = [];
      const newMarkers = [];
      const ring = parcelGeometry.rings[0];
      const minSegmentLength = 5; // Minimum length in feet to show a label
      const angleThreshold = 5; // Degrees off from 180 to consider as straight line

      // Function to calculate angle between three points
      const calculateAngle = (p1, p2, p3) => {
        // Vector from p2 to p1 (reverse direction)
        const v1x = p1[0] - p2[0];
        const v1y = p1[1] - p2[1];

        // Vector from p2 to p3
        const v2x = p3[0] - p2[0];
        const v2y = p3[1] - p2[1];

        // Calculate angle using atan2
        const angle1 = Math.atan2(v1y, v1x);
        const angle2 = Math.atan2(v2y, v2x);

        // Calculate the turning angle (exterior angle)
        let angleDiff = (angle2 - angle1) * (180 / Math.PI);

        // Normalize to 0-360 range
        if (angleDiff < 0) {
          angleDiff += 360;
        }

        // Convert to the angle from straight (180 would be straight)
        // For a right turn (90°), this gives us 90°
        // For straight ahead, this gives us 180°
        return angleDiff;
      };

      // Group consecutive segments that form nearly straight lines
      const segments = [];
      let currentSegment = { start: 0, end: 1 };

      for (let i = 1; i < ring.length - 1; i++) {
        const angle = calculateAngle(ring[i - 1], ring[i], ring[i + 1]);
        console.log(
          `Point ${i}: Angle = ${angle.toFixed(2)}°, Deviation from 180° = ${Math.abs(180 - angle).toFixed(2)}°`
        );

        // If angle is within threshold of 180 degrees, it's nearly straight
        if (Math.abs(180 - angle) <= angleThreshold) {
          console.log(
            `  -> Nearly straight (within ${angleThreshold}° threshold), extending segment`
          );
          // Extend current segment
          currentSegment.end = i + 1;
        } else {
          console.log(`  -> Corner detected, starting new segment`);
          // Save current segment and start a new one
          segments.push({ ...currentSegment });
          currentSegment = { start: i, end: i + 1 };
        }
      }

      // Don't forget the last segment
      segments.push(currentSegment);

      console.log(`\nTotal segments created: ${segments.length}`);
      segments.forEach((seg, idx) => {
        console.log(
          `Segment ${idx}: from point ${seg.start} to ${seg.end} (${seg.end - seg.start} point(s) combined)`
        );
      });

      // Create markers for measurement points
      const markedPoints = new Set();
      segments.forEach((segment) => {
        // Add start point marker
        if (!markedPoints.has(segment.start)) {
          markedPoints.add(segment.start);
          const point = ring[segment.start];
          const marker = new window.google.maps.Marker({
            position: {
              lat: point[1] + offset.lat,
              lng: point[0] + offset.lng,
            },
            map: map,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 6,
              fillColor: '#FF0000',
              fillOpacity: 0.8,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            },
            title: `Point ${segment.start}`,
          });
          newMarkers.push(marker);
        }

        // Add end point marker
        if (!markedPoints.has(segment.end)) {
          markedPoints.add(segment.end);
          const point = ring[segment.end];
          const marker = new window.google.maps.Marker({
            position: {
              lat: point[1] + offset.lat,
              lng: point[0] + offset.lng,
            },
            map: map,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 6,
              fillColor: '#FF0000',
              fillOpacity: 0.8,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            },
            title: `Point ${segment.end}`,
          });
          newMarkers.push(marker);
        }
      });

      // Create labels for each segment
      segments.forEach((segment) => {
        const point1 = ring[segment.start];
        const point2 = ring[segment.end];

        // Skip if points are the same
        if (point1[0] === point2[0] && point1[1] === point2[1]) {
          return;
        }

        // Calculate distance between points
        const lat1 = point1[1] + offset.lat;
        const lng1 = point1[0] + offset.lng;
        const lat2 = point2[1] + offset.lat;
        const lng2 = point2[0] + offset.lng;

        // Using Haversine formula for accurate distance
        const R = 6371e3; // Earth's radius in meters
        const φ1 = (lat1 * Math.PI) / 180;
        const φ2 = (lat2 * Math.PI) / 180;
        const Δφ = ((lat2 - lat1) * Math.PI) / 180;
        const Δλ = ((lng2 - lng1) * Math.PI) / 180;

        const a =
          Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
          Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c; // Distance in meters

        // Convert to feet (1 meter = 3.28084 feet)
        const distanceInFeet = distance * 3.28084;

        // Only show label if segment is long enough
        if (distanceInFeet < minSegmentLength) {
          return;
        }

        // Calculate midpoint for label placement
        const midLat = (lat1 + lat2) / 2;
        const midLng = (lng1 + lng2) / 2;

        // Create a custom overlay for the distance label
        const labelDiv = document.createElement('div');
        labelDiv.className = 'distance-label';
        labelDiv.innerHTML = `<div class="distance-label-text">${distanceInFeet.toFixed(1)} ft</div>`;

        const overlay = new window.google.maps.OverlayView();
        overlay.onAdd = function () {
          const panes = this.getPanes();
          panes.overlayLayer.appendChild(labelDiv);
        };

        overlay.draw = function () {
          const projection = this.getProjection();
          const position = projection.fromLatLngToDivPixel(
            new window.google.maps.LatLng(midLat, midLng)
          );
          labelDiv.style.left = position.x - 40 + 'px';
          labelDiv.style.top = position.y - 10 + 'px';
          labelDiv.style.position = 'absolute';
        };

        overlay.onRemove = function () {
          labelDiv.parentNode.removeChild(labelDiv);
        };

        overlay.setMap(map);
        newLabels.push(overlay);
      });

      setDistanceLabels(newLabels);
      setMeasurementMarkers(newMarkers);
    }, [parcelGeometry, offset, map]);

    // Fit map to parcel bounds when parcel loads and offset is 0
    useEffect(() => {
      if (map && parcelGeometry && offset.lat === 0 && offset.lng === 0) {
        const bounds = getParcelBounds();
        if (bounds) {
          map.fitBounds(bounds);
        }
      }
    }, [map, parcelGeometry, offset, getParcelBounds]);

    const onLoad = useCallback((mapInstance) => {
      setMap(mapInstance);
      mapRef.current = mapInstance;
    }, []);

    const onUnmount = useCallback(() => {
      setMap(null);
    }, []);

    // Expose method to capture map as image
    React.useImperativeHandle(ref, () => ({
      captureMapImage: async () => {
        if (!parcelGeometry) return null;

        try {
          // Get the bounds of the parcel
          let minLat = Infinity,
            maxLat = -Infinity;
          let minLng = Infinity,
            maxLng = -Infinity;

          parcelGeometry.rings[0].forEach((point) => {
            minLng = Math.min(minLng, point[0]);
            maxLng = Math.max(maxLng, point[0]);
            minLat = Math.min(minLat, point[1]);
            maxLat = Math.max(maxLat, point[1]);
          });

          // Apply offset to center
          const centerLat = (minLat + maxLat) / 2 + offset.lat;
          const centerLng = (minLng + maxLng) / 2 + offset.lng;

          // Calculate appropriate zoom level
          const latDiff = maxLat - minLat;
          const lngDiff = maxLng - minLng;
          const maxDiff = Math.max(latDiff, lngDiff);
          let zoom = 19;
          if (maxDiff > 0.001) zoom = 18;
          if (maxDiff > 0.003) zoom = 17;
          if (maxDiff > 0.006) zoom = 16;
          if (maxDiff > 0.012) zoom = 15;

          // Encode the polygon path for Google Maps
          const pathPoints = [];
          const step = Math.max(
            1,
            Math.floor(parcelGeometry.rings[0].length / 30)
          ); // Limit points

          for (let i = 0; i < parcelGeometry.rings[0].length; i += step) {
            const point = parcelGeometry.rings[0][i];
            // Apply offset to each point
            pathPoints.push(
              `${(point[1] + offset.lat).toFixed(6)},${(point[0] + offset.lng).toFixed(6)}`
            );
          }
          // Close the polygon
          const firstPoint = parcelGeometry.rings[0][0];
          pathPoints.push(
            `${(firstPoint[1] + offset.lat).toFixed(6)},${(firstPoint[0] + offset.lng).toFixed(6)}`
          );

          // Build Google Static Maps API URL
          const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap';
          const params = new URLSearchParams({
            center: `${centerLat},${centerLng}`,
            zoom: zoom.toString(),
            size: '640x480', // Google limits free tier to 640x640
            maptype: 'roadmap',
            style: 'feature:poi|visibility:off', // Hide points of interest for cleaner look
          });

          // Add the polygon path
          const pathString = `color:0xff0000ff|weight:3|fillcolor:0xff000033|${pathPoints.join('|')}`;
          params.append('path', pathString);

          // Add API key if available
          const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
          console.log('Google Maps API Key present:', !!apiKey);

          if (apiKey) {
            params.append('key', apiKey);
          }

          const mapUrl = `${baseUrl}?${params.toString()}`;
          console.log('Google Maps URL:', mapUrl);

          // Check if we have Google Maps API key
          if (apiKey) {
            // Return the Google Maps URL directly
            console.log('Returning Google Maps URL for PDF:', mapUrl);
            return mapUrl;
          }

          // Otherwise create our own map visualization (fallback canvas implementation)
          // ... (keeping the existing canvas code for fallback)

          // Create canvas
          const canvas = document.createElement('canvas');
          const width = 800;
          const height = 600;
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');

          // Set white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);

          // Draw a nice looking map background
          const gradient = ctx.createLinearGradient(0, 0, 0, height);
          gradient.addColorStop(0, '#f8f9fa');
          gradient.addColorStop(1, '#e9ecef');
          ctx.fillStyle = gradient;
          ctx.fillRect(0, 0, width, height);

          // ... (rest of the canvas drawing code remains the same)

          // Convert to data URL
          return canvas.toDataURL('image/png');
        } catch (error) {
          console.error('Error generating map image:', error);
          return null;
        }
      },
    }));

    // Handle offset changes
    const handleOffsetChange = (direction) => {
      const step = 0.000005; // About 1-1.5 meters
      const newOffset = { ...offset };

      switch (direction) {
        case 'up':
          newOffset.lat += step;
          break;
        case 'down':
          newOffset.lat -= step;
          break;
        case 'left':
          newOffset.lng -= step;
          break;
        case 'right':
          newOffset.lng += step;
          break;
        case 'reset':
          newOffset.lat = 0;
          newOffset.lng = 0;
          break;
        default:
          break;
      }

      setOffset(newOffset);

      // Notify parent component if needed
      if (onBoundaryUpdate) {
        onBoundaryUpdate(newOffset);
      }
    };

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      return (
        <div className="parcel-map-container" style={{ height }}>
          <div className="map-overlay-message">
            Google Maps API key is required. Please add
            REACT_APP_GOOGLE_MAPS_API_KEY to your .env file.
          </div>
        </div>
      );
    }

    return (
      <div className="parcel-map-container" style={{ height }}>
        <LoadScriptNext
          googleMapsApiKey={apiKey}
          loadingElement={<div>Loading map...</div>}
          preventGoogleFontsLoading={true}
        >
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={defaultCenter}
            zoom={17}
            options={mapOptions}
            onLoad={onLoad}
            onUnmount={onUnmount}
          >
            {/* Polygon is drawn imperatively in the useEffect above */}

            {!parcelGeometry && coordinates && (
              <Marker
                position={{ lat: coordinates.lat, lng: coordinates.lng }}
                title={address}
              />
            )}
          </GoogleMap>
        </LoadScriptNext>

        {!parcelGeometry && (
          <div className="map-overlay-message">
            Search for a property to see the parcel boundaries
          </div>
        )}

        {parcelGeometry && (
          <div className="parcel-adjustment-controls">
            <div className="control-label">Adjust Parcel Position:</div>
            <div className="control-buttons">
              <button
                className="control-btn"
                onClick={() => handleOffsetChange('up')}
                title="Move up"
              >
                ↑
              </button>
              <div className="control-row">
                <button
                  className="control-btn"
                  onClick={() => handleOffsetChange('left')}
                  title="Move left"
                >
                  ←
                </button>
                <button
                  className="control-btn reset-btn"
                  onClick={() => handleOffsetChange('reset')}
                  title="Reset position"
                >
                  ⟲
                </button>
                <button
                  className="control-btn"
                  onClick={() => handleOffsetChange('right')}
                  title="Move right"
                >
                  →
                </button>
              </div>
              <button
                className="control-btn"
                onClick={() => handleOffsetChange('down')}
                title="Move down"
              >
                ↓
              </button>
            </div>
            {(offset.lat !== 0 || offset.lng !== 0) && (
              <div className="offset-indicator">Offset applied</div>
            )}
          </div>
        )}
      </div>
    );
  }
);

ParcelMap.displayName = 'ParcelMap';

export default ParcelMap;
